import { atom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import moment from 'moment';
import { companyIdAtom } from 'views/state';

import { WAType } from './config';
import {
  applyIntervalFilter,
  applyTimespanFilter,
  checkNonZeroArray,
  getAllIndexes,
  getNewDataGenerationDate,
  mergeArrays,
} from '../helper';
import {
  DashboardType,
  GroupingDataItem,
  IntervalFilter,
  IntervalFilterType,
  Severity,
  TimespanFilter,
  VulnerabilityStatus,
  VulnStatus,
} from '../types';

// DASHBOARD FILTERS
export const severityFiltersAtom = atomWithStorage<string[]>(
  'wa_dashboard_severity_filter',
  [
    Severity[Severity.Urgent],
    Severity[Severity.Critical],
    Severity[Severity.Serious],
    Severity[Severity.Medium],
    Severity[Severity.Minimal],
  ],
);
export const showHistoryChartCountsAtom = atomWithStorage(
  'wa_dashboard_show_counts',
  false,
);

export const timeSpanAtom = atomWithReset(TimespanFilter.SixMonth);
export const intervalAtom = atomWithReset(IntervalFilter.Week);
export const waRefreshAtom = atomWithStorage<string>('wa_refresh', '');
export const waCurrentVulnCountAtom = atomWithStorage<string>(
  'waCurrentVulnCount',
  '',
);

export const historyChartsOpenAtom = atom(true);
export const currentVulnChartsOpenAtom = atom(true);

// selected WA's for individual dashboards
export const appliedWAIdsAtom = atomWithReset<number[]>([]);
export const isGroupingByQidVulnListingOpenAtom = atom(false);

export const dashboardDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const appliedWAIds = get(appliedWAIdsAtom);
  const _refresh = get(waRefreshAtom);

  try {
    if (companyId && companyId !== -1) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = {
        company_id: companyId,
        host_type: DashboardType.WAS,
        timezone: userTimezone,
      };
      if (appliedWAIds?.length > 0) {
        params['obj_id_list'] = appliedWAIds;
      }

      // Unique cancellation scope so Web Apps requests aren't aborted by
      // other dashboards that share the same endpoint (e.g. Cloud Agents, Endpoints, SAST).
      // Convention: endpoint/:logical-scope
      const responseData = await callApi(
        'dashboard/',
        'GET',
        params,
        null,
        null,
        'dashboard/:wa-dashboard',
      );
      return responseData?.data;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
});

export const hostCountsAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);
  if (!data?.hosts_counts)
    return {
      [WAType.Webapp]: 0,
    };
  return data.hosts_counts;
});

export const historyChartLabelsAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  return (
    (data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.weeks, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    []
  );
});

export const newDataGenerationIndexAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  const weeks = await get(historyChartLabelsAtom);
  const years =
    (data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.years, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    [];

  const dateInfo = getNewDataGenerationDate(DashboardType.WAS);
  const weekIndexes = getAllIndexes(weeks, dateInfo.week);
  let newDataGenerationIndex = -1;
  weekIndexes.forEach((i) => {
    if (years[i] === dateInfo.year) {
      newDataGenerationIndex = i;
    }
  });
  return newDataGenerationIndex;
});

export const historyChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const severityFilters = get(severityFiltersAtom);
  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);

  if (data) {
    const historyChartSeverityData = {};
    const historyChartLastIntervalSeverityData = {};
    for (const status in VulnStatus) {
      historyChartSeverityData[status] = getHistoryChartData(
        data.historical_data,
        status as VulnStatus,
        severityFilters,
        timespanFilter,
        intervalFilter,
        'severity',
      );
      if (status !== VulnStatus.All) {
        historyChartLastIntervalSeverityData[status] =
          getHistoryChartIntervalData(
            data.historical_data,
            status as VulnStatus,
            severityFilters,
            timespanFilter,
            intervalFilter,
            'last_interval_days',
          );
      }
    }

    return {
      historyChartSeverityData,
      historyChartLastIntervalSeverityData,
    };
  }
  return undefined;
});

export const vulnBySeverityChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  if (data) {
    return getVulnBySeverityChartData(data.vulns_by_severity);
  }
  return undefined;
});

export const vulnGroupsChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnGroupsData(data.vuln_group, severityFilters);
  }
  return undefined;
});

export const severityChartLabelsAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  if (data?.vulns_by_severity) {
    return Object.keys(data.vulns_by_severity[WAType.Webapp]);
  }
  return [];
});

export const LastSyncDateAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  if (data) {
    return moment(data.historical_data?.last_sync_date).format('lll');
  }
  return undefined;
});

export const vulnBySeverityChartDataCountAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnBySeverityChartDataCount(
      data.vulns_by_severity,
      severityFilters,
    );
  }
  return undefined;
});

export const vulnByStatusChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnByStatusChartData(data.vulns_by_status, severityFilters);
  }
  return undefined;
});

export const statusChartLabelsAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  if (data?.vulns_by_status) {
    return Object.keys(
      data?.vulns_by_status[WAType.Webapp][Severity[Severity.Urgent]],
    );
  }
  return [];
});

/*************************/
// HELPER FUNCTIONS
/*************************/

function getVulnByStatusChartData(
  vulnByStatusData: any,
  severityFilters: string[],
) {
  const webapp_values = vulnByStatusData[WAType.Webapp];
  const dataArrays: number[][] = [];
  dataArrays.push(webapp_values);

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);

  const resultArrays: number[][] = [];
  dataArrays.forEach((dataset) => {
    const result = { ACTIVE: 0, NEW: 0, REOPENED: 0, FIXED: 0, IGNORED: 0 };
    selectedSeverityFilters.forEach((severity) => {
      const data = dataset[severity];
      if (data) {
        Object.values(VulnerabilityStatus).forEach((status) => {
          result[status] += data[status];
        });
      }
    });
    resultArrays.push(Object.values(result));
  });

  return resultArrays;
}

function getVulnBySeverityChartDataCount(
  sourceData: any,
  severityFilters: string[],
) {
  const webapp_values = sourceData[WAType.Webapp];

  const dataArrays: number[][] = [];
  const resultsArray: number[][] = [];
  dataArrays.push(webapp_values);

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  dataArrays.forEach((dataset) => {
    const result: number[] = [];
    const severities = ['Urgent', 'Critical', 'Serious', 'Medium', 'Minimal'];
    severities.forEach((severity) => {
      if (selectedSeverityFilters.includes(severity)) {
        result.push(dataset[severity]);
      } else {
        result.push(0);
      }
    });
    resultsArray.push(Object.values(result));
  });
  let count = 0;
  resultsArray.forEach((dataset) => {
    count += dataset.reduce((partialSum, x) => partialSum + x, 0);
  });
  return count;
}

function getVulnGroupsData(vulnGroupsData: any, severityFilters: string[]) {
  const data = vulnGroupsData[WAType.Webapp];
  const dataArrays: any[] = [];
  dataArrays.push(data);

  const result: GroupingDataItem[] = [];

  // severity filter
  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  dataArrays.forEach((dataArray) => {
    selectedSeverityFilters.forEach((severity) => {
      const dArray: GroupingDataItem[] = dataArray[severity];

      if (dArray) {
        dArray.forEach((item) => {
          // if already exists update counts
          const result_items = result.filter((x) => x.qid === item.qid);
          if (result_items?.length > 0) {
            result_items[0].count += item.count;
          }
          // otherwise insert a copy
          else {
            result.push({ ...item });
          }
        });
      }
    });
  });
  return result;
}

function getVulnBySeverityChartData(sourceData: any) {
  const webapp_values = Object.values<number>(sourceData[WAType.Webapp]);

  const resultsArray: number[][] = [];
  if (checkNonZeroArray(webapp_values)) resultsArray.push(webapp_values);

  return resultsArray;
}

function getHistoryChartData(
  historicalData: any,
  status: VulnStatus,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  propertyName: string,
) {
  let DataArray: number[][] = [];
  let selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);

  selectedSeverityFilters.forEach((severityFilter) => {
    DataArray.push(
      historicalData[propertyName][severityFilter]?.[WAType.Webapp][status],
    );
  });

  let Data: number[] = mergeArrays(DataArray);

  const intervalFilterType =
    status === VulnStatus.All
      ? IntervalFilterType.SkipRecords
      : IntervalFilterType.AggregateCounts;

  const resultArray: any[][] = [];
  resultArray.push(
    applyIntervalFilter(
      applyTimespanFilter(Data, timespanFilter),
      intervalFilter,
      intervalFilterType,
    ),
  );
  return resultArray;
}

function getHistoryChartIntervalData(
  historicalData: any,
  status: VulnStatus,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  propertyName: string,
) {
  let DataArray: any[] = [];
  let selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  let statusIntervalFilterKey = `${status.toLowerCase()}_${intervalFilter}`;

  selectedSeverityFilters.forEach((severityFilter) => {
    DataArray.push(
      historicalData[propertyName][severityFilter]?.[WAType.Webapp][
        statusIntervalFilterKey
      ],
    );
  });

  let Data: number[] = DataArray;

  const intervalFilterType = IntervalFilterType.AggregateCounts;

  const resultArray: any[][] = [];
  resultArray.push(
    applyIntervalFilter(Data, intervalFilter, intervalFilterType),
  );
  return resultArray;
}

function getSelectedSeverityFilters(severityFilters: string[]) {
  let selectedSeverityFilters = severityFilters;
  if (severityFilters.includes(Severity[Severity.All])) {
    selectedSeverityFilters = [
      Severity[Severity.Urgent],
      Severity[Severity.Critical],
      Severity[Severity.Medium],
      Severity[Severity.Serious],
      Severity[Severity.Minimal],
    ];
  }
  return selectedSeverityFilters;
}
